export interface CommpeakCallRequest {
  caller: string;
  callee: string;
  callbackUrl?: string;
  timeout?: number;
}

export interface CommpeakCallResponse {
  success: boolean;
  callId?: string;
  message: string;
  error?: string;
}

export interface CallHistory {
  id: string;
  leadId: string;
  phoneNumber: string;
  callId?: string;
  status: 'initiated' | 'connected' | 'completed' | 'failed' | 'no-answer';
  duration?: number;
  startTime: string;
  endTime?: string;
  notes?: string;
  createdBy: string;
}

class CommpeakService {
  private apiToken: string;
  private baseUrl: string = 'https://api.commpeak.com/v1';

  constructor() {
    this.apiToken = '439d5caf4744582379951335e1ac3e18';
  }

  /**
   * Initiate a call through Commpeak API
   */
  async initiateCall(
    callerNumber: string,
    calleeNumber: string,
    leadId?: string
  ): Promise<CommpeakCallResponse> {
    try {
      console.log('Initiating call:', { callerNumber, calleeNumber, leadId });

      const callRequest: CommpeakCallRequest = {
        caller: callerNumber,
        callee: calleeNumber,
        timeout: 30,
        callbackUrl: `${window.location.origin}/api/commpeak/callback`
      };

      const response = await fetch(`${this.baseUrl}/calls/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiToken}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(callRequest)
      });

      const data = await response.json();

      if (response.ok) {
        // Log the call attempt
        if (leadId) {
          await this.logCallHistory({
            leadId,
            phoneNumber: calleeNumber,
            callId: data.callId,
            status: 'initiated',
            startTime: new Date().toISOString(),
            createdBy: 'Admin'
          });
        }

        return {
          success: true,
          callId: data.callId,
          message: 'Call initiated successfully'
        };
      } else {
        throw new Error(data.message || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      
      return {
        success: false,
        message: 'Failed to initiate call',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get call status from Commpeak
   */
  async getCallStatus(callId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/calls/${callId}/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiToken}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to get call status');
      }
    } catch (error) {
      console.error('Error getting call status:', error);
      throw error;
    }
  }

  /**
   * End an active call
   */
  async endCall(callId: string): Promise<CommpeakCallResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/calls/${callId}/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiToken}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: 'Call ended successfully'
        };
      } else {
        throw new Error(data.message || 'Failed to end call');
      }
    } catch (error) {
      console.error('Error ending call:', error);
      return {
        success: false,
        message: 'Failed to end call',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Log call history to localStorage (you can replace with your database)
   */
  private async logCallHistory(callHistory: Omit<CallHistory, 'id'>): Promise<void> {
    try {
      const history = this.getCallHistory();
      const newCall: CallHistory = {
        ...callHistory,
        id: Date.now().toString()
      };
      
      history.push(newCall);
      localStorage.setItem('commpeak_call_history', JSON.stringify(history));
    } catch (error) {
      console.error('Error logging call history:', error);
    }
  }

  /**
   * Get call history from localStorage
   */
  getCallHistory(): CallHistory[] {
    try {
      const history = localStorage.getItem('commpeak_call_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error getting call history:', error);
      return [];
    }
  }

  /**
   * Get call history for a specific lead
   */
  getLeadCallHistory(leadId: string): CallHistory[] {
    const allHistory = this.getCallHistory();
    return allHistory.filter(call => call.leadId === leadId);
  }

  /**
   * Update call history status
   */
  async updateCallStatus(
    callId: string, 
    status: CallHistory['status'], 
    duration?: number,
    notes?: string
  ): Promise<void> {
    try {
      const history = this.getCallHistory();
      const callIndex = history.findIndex(call => call.callId === callId);
      
      if (callIndex !== -1) {
        history[callIndex].status = status;
        history[callIndex].endTime = new Date().toISOString();
        if (duration) history[callIndex].duration = duration;
        if (notes) history[callIndex].notes = notes;
        
        localStorage.setItem('commpeak_call_history', JSON.stringify(history));
      }
    } catch (error) {
      console.error('Error updating call status:', error);
    }
  }

  /**
   * Clear all call history
   */
  clearCallHistory(): void {
    localStorage.removeItem('commpeak_call_history');
  }

  /**
   * Get available caller numbers (you should implement this based on your Commpeak account)
   */
  getAvailableNumbers(): string[] {
    // Replace with actual numbers from your Commpeak account
    return [
      '+1234567890', // Your main business number
      '+1987654321'  // Alternative number
    ];
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phone: string): string {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as international number if it doesn't start with +
    if (!phone.startsWith('+')) {
      return `+${cleaned}`;
    }
    
    return phone;
  }

  /**
   * Validate phone number format
   */
  isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    const cleaned = phone.replace(/\D/g, '');
    return phoneRegex.test(phone) || (cleaned.length >= 7 && cleaned.length <= 15);
  }
}

export const commpeakService = new CommpeakService();
export default commpeakService;